/**
 * React Query Client Configuration
 *
 * Provides centralized QueryClient instance with optimized defaults for BAT.
 * Handles caching, retries, and refetch behavior for server state management.
 *
 * Key configuration decisions:
 * - staleTime: 5min - Data remains "fresh" for 5 minutes to reduce unnecessary refetches
 * - gcTime: 10min - Garbage collection time keeps cached data longer than stale time
 * - retry: 1 - Single retry on failure for better UX without excessive retries
 * - refetchOnWindowFocus: false - Prevents unnecessary refetches when user returns to tab
 */

import { QueryClient } from '@tanstack/react-query'

import { onAcceptedWrite } from '@/platform/api/accepted-writes'


/**
 * Shared retry predicate for React Query queries.
 *
 * Retrying a 4xx client error is wasteful: the request is permanently
 * rejected (validation, auth, not-found), so every retry re-sends a
 * guaranteed failure — extra network + backend load and a slower error state
 * for the UI. Only 429/408 (rate-limit / request-timeout, which the backend
 * lists as retryable) and 5xx / network errors are worth a single retry.
 *
 * Exported so per-hook overrides can reuse it instead of falling back to a
 * blunt numeric `retry` that re-retries 4xx (see `useEffectiveTaxConfig`,
 * whose old `retry: 2` fired a `TENANT_COUNTRY_MISSING` 400 three times).
 *
 * The error parameter is typed as `Error` (React Query v5's DefaultError), NOT
 * `unknown`: the `retry` option sits in a contravariant position, so an
 * `error: unknown` signature would pin a consuming query's `TError` to
 * `unknown` and break `error: query.error` assignments typed as `Error | null`.
 * Typing it as `Error` lets each `useQuery` keep its default `TError = Error`.
 * The `status` field (present on ApiException, not the base `Error` type) is
 * read through a widened `unknown` alias.
 */
export function defaultQueryRetry(failureCount: number, error: Error): boolean {
  const err: unknown = error
  if (err && typeof err === 'object' && 'status' in err) {
    const status = (err as { status: number }).status
    // 429 (Too Many Requests) and 408 (Request Timeout) are transient: back
    // off and retry once. Checked BEFORE the blanket 4xx skip below.
    if (status === 429 || status === 408) {
      return failureCount < 1
    }
    // Don't retry other 4xx client errors (user error, not server).
    if (status >= 400 && status < 500) {
      return false
    }
  }
  // Retry once for 5xx errors and network issues.
  return failureCount < 1
}

/**
 * Query Keys Factory for Transactions
 *
 * Hierarchical key structure enables granular cache invalidation:
 * - Invalidate specific transaction: invalidateQueries(['transactions', 'detail', id])
 * - Invalidate all lists: invalidateQueries(['transactions', 'lists'])
 * - Invalidate everything: invalidateQueries(['transactions'])
 */
export const transactionKeys = {
  all: ['transactions'] as const,
  lists: () => [...transactionKeys.all, 'list'] as const,
  // `sort` is part of the key so a server-side sort change refetches the
  // globally-sorted page (typed `unknown` to avoid a platform→feature import).
  list: (filters: unknown, page: number, pageSize: number, search?: string, sort?: unknown) =>
    [...transactionKeys.lists(), { filters, page, pageSize, search, sort }] as const,
  // The cursor list lives UNDER `lists()`: every invalidation and optimistic edit of the
  // lists reaches it. `sort` is part of the key so a re-sort starts from the first page.
  cursorList: (filters: unknown, search?: string, sort?: unknown) =>
    [...transactionKeys.lists(), 'cursor', { filters, search, sort }] as const,
  // The total of a view: its own query, so the rows never wait for it.
  count: (filters: unknown, search?: string) =>
    [...transactionKeys.all, 'count', { filters, search }] as const,
  detail: (id: string) => [...transactionKeys.all, 'detail', id] as const,
} as const

/**
 * Query Keys Factory for Assets
 */
export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  detail: (id: string) => [...assetKeys.all, 'detail', id] as const,
} as const

/**
 * Query Keys Factory for Balances
 */
export const balanceKeys = {
  all: ['balances'] as const,
  current: () => [...balanceKeys.all, 'current'] as const,
  byAsset: (asset: string) => [...balanceKeys.all, 'asset', asset] as const,
} as const

/**
 * Query Keys Factory for Imports
 */
export const importKeys = {
  all: ['imports'] as const,
  lists: () => [...importKeys.all, 'list'] as const,
  templates: () => [...importKeys.all, 'templates'] as const,
  detail: (id: string) => [...importKeys.all, 'detail', id] as const,
} as const

/**
 * Query Keys Factory for Org Admin
 */
export const orgAdminKeys = {
  all: ['org-admin'] as const,
  stats: () => [...orgAdminKeys.all, 'stats'] as const,
  users: (filters?: unknown) => [...orgAdminKeys.all, 'users', filters] as const,
  userDetail: (id: string) => [...orgAdminKeys.all, 'users', id] as const,
  settings: () => [...orgAdminKeys.all, 'settings'] as const,
  webhooks: (filters?: unknown) => [...orgAdminKeys.all, 'webhooks', filters] as const,
  webhookDetail: (id: string) => [...orgAdminKeys.all, 'webhooks', id] as const,
  webhookStats: () => [...orgAdminKeys.all, 'webhooks', 'stats'] as const,
  webhookDeliveries: (id: string, params?: unknown) =>
    [...orgAdminKeys.all, 'webhooks', id, 'deliveries', params] as const,
  apiKeys: (params?: unknown) => [...orgAdminKeys.all, 'api-keys', params] as const,
  exchanges: () => [...orgAdminKeys.all, 'api-keys', 'exchanges'] as const,
  recentImports: (limit = 10) => [...orgAdminKeys.all, 'recent-imports', limit] as const,
  recentActivity: (limit = 20) => [...orgAdminKeys.all, 'recent-activity', limit] as const,
  recentReports: (params?: { limit?: number; status?: string }) =>
    [...orgAdminKeys.all, 'recent-reports', params] as const,
  alerts: () => [...orgAdminKeys.all, 'alerts'] as const,
  monthlyTransactions: (timeframe: string) =>
    [...orgAdminKeys.all, 'monthly-transactions', timeframe] as const,
  usage: () => [...orgAdminKeys.all, 'usage'] as const,
  // Jobs query keys
  jobs: (filters?: unknown) => [...orgAdminKeys.all, 'jobs', filters] as const,
  jobDetail: (id: string) => [...orgAdminKeys.all, 'jobs', id] as const,
  jobStats: (filters?: unknown) => [...orgAdminKeys.all, 'jobs', 'stats', filters] as const,
  notifications: () => [...orgAdminKeys.all, 'notifications'] as const,
} as const

/**
 * Global QueryClient instance
 *
 * Configured with production-ready defaults:
 * - Optimized for SPA with moderate data volumes
 * - Conservative retry strategy for better UX
 * - Disabled window focus refetch to prevent unnecessary API calls
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data remains fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Single retry on transient failures only; never retry 4xx (see helper).
      retry: defaultQueryRetry,

      // Exponential backoff with jitter for retries
      retryDelay: (attemptIndex) => {
        // Exponential backoff: min(1000 * 2^attemptIndex, 30000) with random jitter
        const delay = Math.min(1000 * 2 ** attemptIndex, 30000)
        const jitter = Math.random() * 200 // Add 0-200ms jitter
        return delay + jitter
      },

      // Don't refetch on window focus (reduces unnecessary API calls)
      refetchOnWindowFocus: false,

      // Refetch on remount if data is stale
      refetchOnMount: true,

      // Don't refetch on reconnect by default (let user initiate)
      refetchOnReconnect: false,
    },
    mutations: {
      // Mutations are non-idempotent by default (create/delete/discard/resolve),
      // so retrying a write that already committed server-side but failed
      // client-side (network blip, or a false-error) DUPLICATES the effect or
      // surfaces a spurious 409. This matches React Query's own default
      // (`retry: false`). Idempotent mutations can opt back in per-hook.
      retry: false,
    },
  },

  // Note: Global error handlers are configured per-query/mutation in defaultOptions
  // The cache instances themselves don't expose onError directly in v5
})

/**
 * The ledger changed: every cached read goes stale at once. Visible queries refetch now, the
 * others on their next mount, so no screen shows data a write just changed (staleTime above
 * only spares refetches of data nothing changed). Session calls change no data.
 */
export function invalidateEverything(): void {
  void queryClient.invalidateQueries()
}

onAcceptedWrite((endpoint) => {
  if (!endpoint.includes('/auth/')) invalidateEverything()
})
